import { buildCriterionExpression, Sort as HeadlessSort } from "@coveo/headless";
import { useEffect, useMemo, useRef, useState } from "react";
import { SortByDictionary } from "components/coveo-headless/types/coveo-headless-types";
import { ImageCaret } from "components/utility/images/ImageCaret";

type PublicSearchPageSortByProps = {
	criteriaDictionary: SortByDictionary[];
	controller: HeadlessSort;
};

const PublicSearchPageSortBy = ({
	controller,
	criteriaDictionary
}: PublicSearchPageSortByProps) => {
	const [state, setState] = useState(controller.state);
	const [isFilterBoxOpen, setIsFilterBoxOpen] = useState<boolean>(false);
	const filterBoxRef = useRef<HTMLDivElement>(null);

	const sortBySelectionClassName = useMemo<string>(
		() =>
			isFilterBoxOpen
				? "public-search-sort-by-selection active"
				: "public-search-sort-by-selection",
		[isFilterBoxOpen]
	);

	useEffect(() => controller.subscribe(() => setState(controller.state)), [controller]);

	useEffect(() => {
		const filterBox = filterBoxRef.current;

		const handleOutsideClick: EventListener = (event): void => {
			if (!filterBox?.contains(event.target as Node)) {
				setIsFilterBoxOpen(false);
			}
		};

		if (filterBox && isFilterBoxOpen) {
			window.addEventListener("click", handleOutsideClick);
		}

		return () => {
			window.removeEventListener("click", handleOutsideClick);
		};
	}, [isFilterBoxOpen, setIsFilterBoxOpen]);

	const getCurrentCriterionName = () => {
		const criterion = criteriaDictionary.find(
			(c) => state.sortCriteria === buildCriterionExpression(c.criterion)
		);
		return criterion ? criterion.name : "";
	};

	return (
		<div className="public-search-sort-by-container" ref={filterBoxRef}>
			<div
				className={sortBySelectionClassName}
				role="presentation"
				onClick={() => {
					setIsFilterBoxOpen((isFilterBoxOpen) => !isFilterBoxOpen);
				}}
				onKeyUp={(event): void => {
					const allowedKeys = new Set(["Enter", "Space"]);
					if (!allowedKeys.has(event.code)) return;

					event.preventDefault();
					setIsFilterBoxOpen((isFilterBoxOpen) => !isFilterBoxOpen);
				}}
			>
				<span className="public-search-sort-by-selection-text t-label-xs-heavy">{`sort by: ${getCurrentCriterionName()}`}</span>
				<div className="public-search-sort-by-selection-caret">
					<ImageCaret />
				</div>
			</div>
			{isFilterBoxOpen ? (
				<ul className={`public-search-sort-by-options`}>
					{criteriaDictionary.map((sortInfo) => (
						<li
							className="t-label-xs-heavy tc-cool-gray-02"
							aria-label={`sort by ${sortInfo.name}`}
							key={sortInfo.name}
							onClick={() => {
								controller.sortBy(sortInfo.criterion);
								setIsFilterBoxOpen(false);
							}}
							onKeyUp={() => {
								controller.sortBy(sortInfo.criterion);
							}}
							role="presentation"
							value={sortInfo.name}
						>
							{sortInfo.name}
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
};

export default PublicSearchPageSortBy;
